import React, { useState, useEffect } from 'react';
import { Box, Alert, Typography } from '@mui/material';

const RECAPTCHA_SITE_KEY = '6LcZAkwrAAAAAA7gZjMP_OT6IGBEwPYmaSQsg7a4';

// Validation utilities
const validationUtils = {
    isValidEmail: (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },
    
    isValidPhone: (phone) => {
        const phoneRegex = /^[0-9\-+()\s]{10,15}$/;
        return phoneRegex.test(phone);
    },
    
    isValidZipCode: (zip) => {
        const zipRegex = /^\d{5}(-\d{4})?$/;
        return zipRegex.test(zip);
    },
    
    isValidDate: (date) => {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(date)) return false;
        
        const d = new Date(date);
        return d instanceof Date && !isNaN(d);
    },
    
    sanitizeInput: (input) => {
        if (typeof input !== 'string') return input;
        return input.trim().replace(/[<>]/g, '');
    },

    // Map field names to their base types for length validation
    getFieldType: (fieldName) => {
        // Handle compound field names
        if (fieldName.includes('FirstName') || fieldName.includes('LastName') || fieldName.includes('MiddleName')) {
            return 'name';
        }
        if (fieldName.includes('Email')) {
            return 'email';
        }
        if (fieldName.includes('Phone')) {
            return 'phone';
        }
        if (fieldName.includes('Address')) {
            return 'address';
        }
        if (fieldName.includes('City')) {
            return 'city';
        }
        if (fieldName.includes('State')) {
            return 'state';
        }
        if (fieldName.includes('ZipCode')) {
            return 'zipCode';
        }
        if (fieldName.includes('School')) {
            return 'school';
        }
        if (fieldName.includes('GradeLevel')) {
            return 'gradeLevel';
        }
        if (fieldName.includes('SchoolYear')) {
            return 'schoolYear';
        }
        if (fieldName.includes('HowDidYouHear')) {
            return 'howDidYouHearAboutUs';
        }
        if (fieldName.includes('DateOfBirth')) {
            return 'dateOfBirth';
        }
        if (fieldName.includes('Gender')) {
            return 'gender';
        }
        if (fieldName.includes('Salutation')) {
            return 'salutation';
        }
        if (fieldName.includes('SuiteNumber')) {
            return 'suiteNumber';
        }
        // Return the original field name if no mapping is found
        return fieldName;
    },

    // Input length limits
    MAX_LENGTHS: {
        name: 100,
        email: 254, // RFC 5321
        phone: 15,
        message: 5000,
        address: 200,
        suiteNumber: 20,
        city: 100,
        state: 50,
        zipCode: 10,
        school: 200,
        gradeLevel: 50,
        schoolYear: 50,
        howDidYouHearAboutUs: 200,
        dateOfBirth: 10, // YYYY-MM-DD format
        gender: 40,
        salutation: 30
    }
};

const BaseForm = ({ 
    children, 
    onSubmit, 
    title,
    subtitle,
    successMessage = 'Thank you for your message! We will get back to you soon.',
    errorMessage = 'Sorry, there was an error sending your message. Please try again later.'
}) => {
    const [submitStatus, setSubmitStatus] = useState({
        success: false,
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [honeypotValue, setHoneypotValue] = useState('');

    const loadRecaptcha = async () => {
        try {
            if (!window.grecaptcha) {
                const script = document.createElement('script');
                script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
                script.async = true;
                script.defer = true;
                
                await new Promise((resolve, reject) => {
                    script.onload = () => {
                        window.grecaptcha.ready(() => {
                            resolve();
                        });
                    };
                    
                    script.onerror = (error) => {
                        console.error('Script load error:', error);
                        reject(new Error('Failed to load reCAPTCHA script'));
                    };
                    
                    document.body.appendChild(script);
                });
            } else {
                await new Promise((resolve, reject) => {
                    if (typeof window.grecaptcha.ready === 'function') {
                        window.grecaptcha.ready(() => {
                            resolve();
                        });
                    } else {
                        console.error('grecaptcha.ready is not a function');
                        reject(new Error('reCAPTCHA not properly initialized'));
                    }
                });
            }
        } catch (error) {
            console.error('Error in loadRecaptcha:', error);
            throw error;
        }
    };

    useEffect(() => {
        return () => {
            const script = document.querySelector(`script[src*="recaptcha/api.js"]`);
            if (script) {
                document.body.removeChild(script);
            }

            const badge = document.querySelector('.grecaptcha-badge');
            if (badge) {
                badge.remove();
            }

            const iframes = document.querySelectorAll('iframe[src*="recaptcha"]');
            iframes.forEach(iframe => iframe.remove());

            if (window.grecaptcha) {
                delete window.grecaptcha;
            }

            const styles = document.querySelectorAll('style[data-recaptcha]');
            styles.forEach(style => style.remove());
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Form submission started');

        // Validate form before starting submission
        const form = e.target;

        setIsSubmitting(true);

        try {
            // Check honeypot field
            if (honeypotValue !== '') {
                throw new Error('Invalid form submission');
            }

            // Get form data
            const formData = new FormData(form);
            const formValues = Object.fromEntries(formData.entries());
            
            // Basic sanitization of all inputs
            const sanitizedData = Object.entries(formValues).reduce((acc, [key, value]) => {
                acc[key] = validationUtils.sanitizeInput(value);
                return acc;
            }, {});
            
            // Validate email fields
            const emailFields = Array.from(form.querySelectorAll('input[type="email"]')).map(field => field.name);
            const invalidEmails = emailFields.filter(field => {
                const value = sanitizedData[field];
                // Only validate format if the field is not empty
                return value && !validationUtils.isValidEmail(value);
            });
            
            if (invalidEmails.length > 0) {
                throw new Error('Please enter a valid email address');
            }
            // Validate phone fields
            const phoneFields = Array.from(form.querySelectorAll('input[name*="phone"]')).map(field => field.name);
            const invalidPhones = phoneFields.filter(field => {
                const value = sanitizedData[field];
                // Only validate format if the field is not empty
                return value && !validationUtils.isValidPhone(value);
            });
            
            if (invalidPhones.length > 0) {
                throw new Error('Please enter a valid phone number');
            }
            
            // Validate zip code if present and not empty
            if (sanitizedData.zipCode && !validationUtils.isValidZipCode(sanitizedData.zipCode)) {
                throw new Error('Please enter a valid ZIP code');
            }
            
            // Validate date if present and not empty
            if (sanitizedData.dateOfBirth && !validationUtils.isValidDate(sanitizedData.dateOfBirth)) {
                throw new Error('Please enter a valid date');
            }

            // Validate field lengths (only for non-empty fields)
            const lengthErrors = Object.entries(sanitizedData)
                .filter(([key, value]) => {
                    if (!value) return false;
                    const fieldType = validationUtils.getFieldType(key);
                    return validationUtils.MAX_LENGTHS[fieldType] && value.length > validationUtils.MAX_LENGTHS[fieldType];
                })
                .map(([key, value]) => {
                    // Convert field names to user-friendly labels
                    const fieldLabels = {
                        name: 'Name',
                        email: 'Email',
                        phone: 'Phone number',
                        message: 'Message',
                        address: 'Address',
                        city: 'City',
                        state: 'State',
                        zipCode: 'ZIP code',
                        school: 'School name',
                        gradeLevel: 'Grade level',
                        schoolYear: 'School year',
                        howDidYouHearAboutUs: 'How did you hear about us',
                        dateOfBirth: 'Date of birth',
                        gender: 'Gender',
                        salutation: 'Salutation',
                        suiteNumber: 'Suite/Apartment number'
                    };
                    const fieldType = validationUtils.getFieldType(key);
                    const label = fieldLabels[fieldType] || key;
                    return `${label} is too long`;
                });

            if (lengthErrors.length > 0) {
                throw new Error(lengthErrors.join(', '));
            }

            // Only load and execute reCAPTCHA if all validations pass
            await loadRecaptcha();
            const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {action: 'submit'});
            
            const result = await onSubmit(token);
            
            if (result.success) {
                console.log('Form submission successful');
                setSubmitStatus({
                    success: true,
                    message: successMessage
                });
            } else if (result.validationError) {
                setSubmitStatus({
                    success: false,
                    message: result.error || 'Please fix the form errors'
                });
            } else {
                throw new Error(result.error || 'Failed to submit form');
            }
        } catch (error) {
            console.error('Error in form submission:', error);
            setSubmitStatus({
                success: false,
                message: error.message || errorMessage
            });
        } finally {
            console.log('Form submission process completed');
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                maxWidth: '100%',
                width: '100%',
                mx: 'auto',
                p: 2,
                boxSizing: 'border-box',
                position: 'relative',
                overflow: 'hidden'
            }}
            noValidate
        >
            {/* Honeypot field - hidden from real users but visible to bots */}
            <div style={{ display: 'none' }}>
                <input
                    type="text"
                    name="website"
                    value={honeypotValue}
                    onChange={(e) => setHoneypotValue(e.target.value)}
                    autoComplete="off"
                    tabIndex="-1"
                />
            </div>

            {title && (
                <Typography variant="h6" sx={{ mb: 1, textAlign: 'left' }}>
                    {title}
                </Typography>
            )}
            {subtitle && (
                <Typography variant="h6" sx={{ mb: 3, textAlign: 'left' }}>
                    {subtitle}
                </Typography>
            )}
            {typeof children === 'function' ? children({ isSubmitting }) : children}
            {submitStatus.success && (
                <Alert severity="success" sx={{ mt: 2, mb: 2, width: '100%', boxSizing: 'border-box' }}>
                    {submitStatus.message}
                </Alert>
            )}
            {!submitStatus.success && submitStatus.message && (
                <Alert severity="error" sx={{ mt: 2, mb: 2, width: '100%', boxSizing: 'border-box' }}>
                    {submitStatus.message}
                </Alert>
            )}
        </Box>
    );
};

export default BaseForm; 